import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import ContainerDimensions from 'react-container-dimensions';
import Immutable, {Map} from 'immutable';
import immutableDevtools from 'immutable-devtools';
import {createStore} from 'redux';
import {Provider} from 'react-redux';

import MyCatalog from './catalog/mycatalog';
import logoLoghouse from './assets/logo-loghouse.png';

import ToolbarScreenshotButton from './ui/toolbar-screenshot-button';
import ToolbarPdfExportButton from './ui/toolbar-pdf-export-button';

import {
  Models as PlannerModels,
  reducer as PlannerReducer,
  ReactPlanner,
  Plugins as PlannerPlugins,
} from 'architecrange';

//define state
let AppState = Map({
  architecrange: new PlannerModels.State()
});

//define reducer
let reducer = (state, action) => {
  state = state || AppState;
  state = state.update('architecrange', plannerState => PlannerReducer(plannerState, action));
  return state;
};

let blackList = isProduction === true ? [] : [
  'UPDATE_MOUSE_COORDS',
  'UPDATE_ZOOM_SCALE',
  'UPDATE_2D_CAMERA'
];

if( !isProduction ) {
  console.info('Environment is in development and these actions will be blacklisted', blackList);
  console.info('Enable Chrome custom formatter for Immutable pretty print');
  immutableDevtools( Immutable );
}

//init store
let store = createStore(
  reducer,
  null,
  !isProduction && window.devToolsExtension ?
    window.devToolsExtension({
      features: {
        pause   : true,     // start/pause recording of dispatched actions
        lock    : true,     // lock/unlock dispatching actions and side effects
        persist : true,     // persist states on page reloading
        export  : true,     // export history of actions in a file
        import  : 'custom', // import history of actions from a file
        jump    : true,     // jump back and forth (time travelling)
        skip    : true,     // skip (cancel) actions
        reorder : true,     // drag and drop actions in the history list
        dispatch: true,     // dispatch custom actions or action creators
        test    : true      // generate tests for the selected actions
      },
      actionsBlacklist: blackList,
      maxAge: 999999
    }) :
    f => f
);

const projectApiBase =
  typeof window !== 'undefined' && window.location && new URLSearchParams(window.location.search).get('projectApi');

let plugins = [
  PlannerPlugins.Keyboard(),
  projectApiBase
    ? PlannerPlugins.AutosaveApi(projectApiBase, 'default')
    : PlannerPlugins.Autosave('architecrange_v0'),
  PlannerPlugins.ConsoleDebugger(),
];

let toolbarButtons = [ToolbarScreenshotButton, ToolbarPdfExportButton];

const LOGHOUSE_HOME = 'https://loghouse.rodalbores.com/';
const MOBILE_VIEWPORT_MQ = '(max-width: 767px)';

function useIsMobileViewport() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(MOBILE_VIEWPORT_MQ).matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_VIEWPORT_MQ);
    const sync = () => setIsMobile(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  return isMobile;
}

function LoghouseTopNav() {
  const upper = {
    position: 'sticky',
    top: 0,
    zIndex: 60,
    width: '100%',
    background: '#fff',
    color: '#23150d',
    fontFamily: 'system-ui, "Helvetica Neue", Helvetica, Arial, sans-serif',
    borderBottom: '1px solid #e5e7eb',
  };
  const upperInner = {
    maxWidth: 1600,
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 48,
    padding: '0 12px',
    boxSizing: 'border-box',
  };
  const navBar = {
    position: 'sticky',
    top: 48,
    zIndex: 50,
    width: '100%',
    background: '#402f02',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    backdropFilter: 'blur(12px)',
  };
  const navInner = {
    maxWidth: 1600,
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 40,
    padding: '0 16px',
    boxSizing: 'border-box',
  };
  const backLink = {
    color: '#23150d',
    textDecoration: 'none',
    fontSize: 13,
    fontWeight: 500,
  };
  const rangeLabel = {
    color: '#c2b397',
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: '0.02em',
  };
  return (
    <div>
      <div style={upper}>
        <div style={upperInner}>
          <a href={LOGHOUSE_HOME} style={{display: 'flex', alignItems: 'center'}}>
            <img src={logoLoghouse} alt="LogHouse Group" style={{height: 40, objectFit: 'contain'}} />
          </a>
          <a href={LOGHOUSE_HOME} style={backLink}>Back to Home</a>
        </div>
      </div>
      <div style={navBar}>
        <div style={navInner}>
          <span style={rangeLabel}>Architect Range</span>
        </div>
      </div>
    </div>
  );
}

function MobileDesktopPrompt() {
  const isMobile = useIsMobileViewport();
  if (!isMobile) return null;
  const bar = {
    width: '100%',
    padding: '10px 14px',
    boxSizing: 'border-box',
    background: '#fef3c7',
    color: '#78350f',
    fontFamily: 'system-ui, "Helvetica Neue", Helvetica, Arial, sans-serif',
    fontSize: 13,
    fontWeight: 500,
    textAlign: 'center',
    borderBottom: '1px solid #fcd34d',
  };
  return <div style={bar}>Architect Range is best for desktop screens</div>;
}

//render
ReactDOM.render(
  (
    <Provider store={store}>
      <div style={{display: 'flex', flexDirection: 'column', height: '100%', width: '100%'}}>
        <LoghouseTopNav />
        <MobileDesktopPrompt />
        <div style={{flex: 1, minHeight: 0, width: '100%'}}>
          <ContainerDimensions>
            {({width, height}) =>
              <ReactPlanner
                catalog={MyCatalog}
                width={width}
                height={height}
                plugins={plugins}
                toolbarButtons={toolbarButtons}
                stateExtractor={state => state.get('architecrange')}
              />
            }
          </ContainerDimensions>
        </div>
      </div>
    </Provider>
  ),
  document.getElementById('app')
);

